import React, { useState } from 'react';
import { Link, useParams, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import PrivacyPolicyModal from './PrivacyPolicyModal';
import TermsOfUseModal from './TermsOfUseModal';
import '../styles/_footer.scss';

const Footer = () => {
  const { t } = useTranslation();
  const { language } = useParams();
  const location = useLocation();
  const [isPrivacyPolicyOpen, setIsPrivacyPolicyOpen] = useState(false);
  const [isTermsOfUseOpen, setIsTermsOfUseOpen] = useState(false);

  const openPrivacyPolicy = (e) => {
    e.preventDefault();
    setIsPrivacyPolicyOpen(true);
  };

  const openTermsOfUse = (e) => {
    e.preventDefault();
    setIsTermsOfUseOpen(true);
  };
  
  const scrollToContact = (e) => {
    // Якщо ми вже на головній сторінці
    if (location.pathname === `/${language}` || location.pathname === `/${language}/`) {
      e.preventDefault();
      const contactSection = document.getElementById('contact-us');
      if (contactSection) {
        contactSection.scrollIntoView({ behavior: 'smooth' });
      }
    }
  };

  // Функція для створення локалізованого посилання
  const getLocalizedLink = (path) => {
    return `/${language}${path === '/' ? '' : path}`;
  };

  return (
    <footer className="footer">
      <div className="container">
        <div className="footer-content">
          <div className="footer-logo">
            <Link to={getLocalizedLink('/')} className="logo">
              <span className="logo-text">AI PSY HELP</span>
            </Link>
            <p className="footer-tagline">
              {t('footer.tagline')}
            </p>
          </div>
          
          <div className="footer-links">
            <div className="footer-links-column">
              <h4 className="footer-links-title">{t('footer.navigation_title')}</h4>
              <ul className="footer-links-list">
                <li><Link to={getLocalizedLink('/')}>{t('nav.home')}</Link></li>
                <li><Link to={getLocalizedLink('/news')}>{t('nav.news')}</Link></li>
                <li><Link to={getLocalizedLink('/videos')}>{t('nav.videos')}</Link></li>
              </ul>
            </div>
            
            <div className="footer-links-column">
              <h4 className="footer-links-title">{t('footer.solutions_title')}</h4>
              <ul className="footer-links-list">
                <li><Link to={getLocalizedLink('/corporate')}>{t('nav.corporate')}</Link></li>
                <li><Link to={getLocalizedLink('/ngo')}>{t('nav.ngo')}</Link></li>
                <li><Link to={getLocalizedLink('/webinars')}>{t('nav.webinars')}</Link></li>
              </ul>
            </div>
            
            <div className="footer-links-column">
              <h4 className="footer-links-title">{t('footer.docs_title')}</h4>
              <ul className="footer-links-list">
                <li><a href="#privacy" onClick={openPrivacyPolicy}>{t('footer.privacy')}</a></li>
                <li><a href="#terms" onClick={openTermsOfUse}>{t('footer.terms')}</a></li>
                <li><Link to={getLocalizedLink('/#contact-us')} onClick={scrollToContact}>{t('footer.contact')}</Link></li>
              </ul>
            </div>
          </div>
        </div>
        
        <div className="footer-social">
          <h4 className="footer-social-title">Follow Us</h4>
          <div className="social-icons">
            <a href="https://www.linkedin.com/company/aipsyhelp" target="_blank" rel="noopener noreferrer" className="social-icon linkedin" aria-label="LinkedIn">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="currentColor"><path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z"/></svg>
            </a>
            <a href="https://www.facebook.com/PsyHelpUA/" target="_blank" rel="noopener noreferrer" className="social-icon facebook" aria-label="Facebook">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="currentColor"><path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-3 7h-1.924c-.615 0-1.076.252-1.076.889v1.111h3l-.238 3h-2.762v8h-3v-8h-2v-3h2v-1.923c0-2.022 1.064-3.077 3.461-3.077h2.539v3z"/></svg>
            </a>
            <a href="https://www.youtube.com/@AIPSYHELP" target="_blank" rel="noopener noreferrer" className="social-icon youtube" aria-label="YouTube">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="currentColor"><path d="M19.615 3.184c-3.604-.246-11.631-.245-15.23 0-3.897.266-4.356 2.62-4.385 8.816.029 6.185.484 8.549 4.385 8.816 3.6.245 11.626.246 15.23 0 3.897-.266 4.356-2.62 4.385-8.816-.029-6.185-.484-8.549-4.385-8.816zm-10.615 12.816v-8l8 3.993-8 4.007z"/></svg>
            </a>
            <a href="https://www.tiktok.com/@ai.psy.help" target="_blank" rel="noopener noreferrer" className="social-icon tiktok" aria-label="TikTok">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="currentColor"><path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z"/></svg>
            </a>
          </div>
        </div>
        
        <div className="footer-bottom">
          <p className="copyright">{t('footer.copyright')}</p>
        </div>
      </div>
      
      <PrivacyPolicyModal 
        isOpen={isPrivacyPolicyOpen} 
        onClose={() => setIsPrivacyPolicyOpen(false)} 
      />
      
      <TermsOfUseModal 
        isOpen={isTermsOfUseOpen} 
        onClose={() => setIsTermsOfUseOpen(false)} 
      />
    </footer>
  );
};

export default Footer; 